import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {BlockSdkService} from '../../services/block-sdk.service';
import {environment} from '../../../environments/environment';
import {BlockAuthService} from '../../services/block-auth.service';
import {catchError} from 'rxjs/operators';
import {Observable, throwError} from 'rxjs';
import {BusinessUnitService} from '../../services/business-unit.service';
import {HttpErrorResponse} from '@angular/common/http';
import {ColorSwatch, getQuillColors, rgbToHex} from '../../types/color-swatch';
import {QuillEditorComponent, QuillModules} from 'ngx-quill';
import {PaymentService} from '../../services/payment.service';
import {MergeField} from '../../types/merge-field';
import {ColorPalette} from '../../types/color-palette';
import {SubscriptionPlanType} from '../../types/plan-type.enum';

@Component({
  selector: 'app-color-picker-editor',
  templateUrl: './color-picker-editor.component.html',
  styleUrls: ['./color-picker-editor.component.css']
})
export class ColorPickerEditorComponent implements OnInit {
  textColors: ColorSwatch[] = [];
  authError: string;
  noLicenceAlert: boolean;
  isLoadingEditor = true;

  toolbarOptions: QuillModules = { // TODO do not create unless there is data
    // @ts-ignore
    toolbar: {
      container: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'merge-fields': MergeFields.getFieldIndexesList() }],
        ['blockquote', 'code-block'],
        [{ list: 'ordered'}, { list: 'bullet' }],
        [{ indent: '-1'}, { indent: '+1' }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ color: [] }, { background: [] }],
        [{'premium-color': ['0', '1', '2', '3']}, {'premium-background': ['0', '1', '2', '3']}],
        ['clean'],
        ['link']
        ] ,
      handlers: {
        'merge-fields': this.mergeFields.bind(this),
        'premium-color' : this.premiumColor.bind(this),
        'premium-background' : this.premiumBackground.bind(this),
      }
    }
  };
  @ViewChild('editor') editor: QuillEditorComponent;
  content = '';
  isInIframe: boolean;
  isEnvironmentProduction: boolean;
  showNoAccountAlert = false;
  private sdk;
  private colorSwatches: ColorPalette[];
  premiumColors: PremiumColors;

  constructor(private blockSdkService: BlockSdkService, private businessUnitService: BusinessUnitService,
              private blockAuth: BlockAuthService, private billingAccountService: PaymentService, private elem: ElementRef) {}

  ngOnInit(): void {
    this.isInIframe = window.self !== window.top;
    this.isEnvironmentProduction = environment.production;
    this.sdk = this.blockSdkService.createNewBlockSDK();
    const sub = this.blockAuth.getAuthToken().subscribe(() => {
      this.billingAccountService.getBillingAccountState().subscribe(accountState => {
      this.blockAuth.getUserData().subscribe(data => console.log(data));
      const businessUnit = this.businessUnitService.getBusinessUnit().pipe(catchError(this.noAccountExists.bind(this)));
      this.businessUnitService.getColorPalettes().subscribe(colorSwatches => {
        this.colorSwatches = colorSwatches;
        this.premiumColors = new PremiumColors(colorSwatches);
        this.textColors = colorSwatches[0].color_swatches;

        // @ts-ignore
        this.toolbarOptions = { toolbar: { container: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'merge-fields': MergeFields.getFieldIndexesList() }],
            ['blockquote', 'code-block'],
            [{ list: 'ordered'}, { list: 'bullet' }],
            [{ indent: '-1'}, { indent: '+1' }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            ['clean'],
            ['link']
          ] }

        };

        if (accountState.billingAccountState.paymentStatus === SubscriptionPlanType.PREMIUM){
        // @ts-ignore
        this.toolbarOptions.toolbar.container.splice(6, 0, [
          {'premium-color': PremiumColors.getLengthColorPaletteIndexes('text_color', this.colorSwatches)},
          {'premium-background': PremiumColors.getLengthColorPaletteIndexes('text_background', this.colorSwatches)}]);
        // @ts-ignore
        this.toolbarOptions.toolbar.handlers =  {
          'merge-fields': this.mergeFields.bind(this),
          'premium-color' : this.premiumColor.bind(this),
          'premium-background' : this.premiumBackground.bind(this),
        };

      }else {
        // @ts-ignore
        this.toolbarOptions.toolbar.container.splice(6, 0, [
          { color: getQuillColors(this.textColors, 'text_color') },
          { background: getQuillColors(this.textColors, 'text_background') }
          ], );
      }

        businessUnit.subscribe(data => console.log(data));

        if (!accountState.billingAccountState.accountExists || accountState.billingAccountState.paymentStatus === `free`) {
          this.noLicenceAlert = true;
        }
        this.isLoadingEditor = false;

      });
      });
    }, error => {
      this.isLoadingEditor = false;
      this.authError = error.error_description;
    });
    setTimeout(() => {
      this.isLoadingEditor = false;
      sub.unsubscribe();
    }, 6000);
    this.quillInit();
  }
  noAccountExists(errorResponse: HttpErrorResponse): Observable<any>{
      console.error('Unable to get account:', errorResponse.error.error);
      if (errorResponse.error.error === 'no_account'){
        this.showNoAccountAlert = true;
      }
      return throwError(
      'Unable to get Business Account Information');
  }
  premiumBackground(value: any): void {
    const quill = this.editor.quillEditor;
    const range = quill.getSelection();
    if (range) {
      if ( typeof value === 'string') {
        if (!this.colorSwatches){
          return;
        }
        const colorPaletteForIndex = this.premiumColors.getColorPaletteForIndex(Number(value), 'text_background');

        if (typeof colorPaletteForIndex   === 'string'){
          return;
        }
        quill.format('background', rgbToHex(
          // @ts-ignore
          colorPaletteForIndex.r,
          // @ts-ignore
          colorPaletteForIndex.g,
          // @ts-ignore
          colorPaletteForIndex.b));
      }
    }
  }
  premiumColor(value: any): void {
    const quill = this.editor.quillEditor;
    const range = quill.getSelection();
    if (range) {
      if ( typeof value === 'string') {
        if (!MergeFields.isClickable(value)) {
          return;
        }
        if (!this.colorSwatches){
          return;
        }
        const colorPaletteForIndex = this.premiumColors.getColorPaletteForIndex(Number(value), 'text_color');

        if (typeof colorPaletteForIndex   === 'string'){
          return;
        }else {

          quill.format('color', rgbToHex(
            colorPaletteForIndex.r,
            colorPaletteForIndex.g,
            colorPaletteForIndex.b));
        }
      }
    }
  }

  getEditor(event: any): void {
    const placeholderPickerItems = Array.prototype.slice.call(document.querySelectorAll('.ql-merge-fields .ql-picker-item'));

    placeholderPickerItems.forEach((item: HTMLElement) => {
      item.textContent = MergeFields.getDisplayValue(item.dataset.value);
      if ( MergeFields.getDisplayValue(item.dataset.value) === '$sep' ) {
        item.textContent = '';
        item.classList.add( 'sep-solid' );
      }
    });
    let cpi = 0;
    const placeholderColorItems = Array.prototype.slice.call(document.querySelectorAll('.ql-premium-color .ql-picker-item'));
    placeholderColorItems.forEach((item: HTMLElement) => {
      if (!this.colorSwatches){
        return;
      }
      const colorSwatch = this.premiumColors.getColorPaletteForIndex(cpi, 'text_color');
      if (!(typeof colorSwatch   === 'string')) {

        const bbcolor = `rgb(${colorSwatch.r}, ${colorSwatch.g}, ${colorSwatch.b})`;
        item.style.backgroundColor = bbcolor;
      }else {

        item.textContent =  colorSwatch ;
        item.classList.add('cp-text-display');
      }
      cpi++;
    });
    let bpi = 0;
    const placeholderBackgroundItems = Array.prototype.slice.call(document.querySelectorAll('.ql-premium-background .ql-picker-item'));
    placeholderBackgroundItems.forEach((item: HTMLElement) => {
      if (!this.colorSwatches){
        return;
      }
      const colorSwatch = this.premiumColors.getColorPaletteForIndex(bpi, 'text_background');
      if (!(typeof colorSwatch   === 'string')) {

        const bbcolor = `rgb(${colorSwatch.r}, ${colorSwatch.g}, ${colorSwatch.b})`;
        item.style.backgroundColor = bbcolor;
      }else {

        item.textContent =  colorSwatch ;
        item.classList.add('cp-text-display');
      }
      bpi++;
    });

    document.querySelector('.ql-merge-fields .ql-picker-label ').innerHTML
      = '<span class="fuelux-icon fuelux-icon-person"></span>' + document.querySelector('.ql-merge-fields .ql-picker-label').innerHTML;

    document.querySelector('.ql-premium-color .ql-picker-label ').innerHTML
      = '<svg viewBox="0 0 18 18"> <line class="ql-premium-color-label ql-stroke ql-transparent" x1="3" x2="15" y1="15" y2="15"></line> <polyline class="ql-stroke" points="5.5 11 9 3 12.5 11"></polyline> <line class="ql-stroke" x1="11.63" x2="6.38" y1="9" y2="9"></line> </svg>';

    document.querySelector('.ql-premium-background .ql-picker-label ').innerHTML
      = '<svg viewBox="0 0 18 18"> <g class="ql-fill ql-premium-color-label"> <polygon points="6 6.868 6 6 5 6 5 7 5.942 7 6 6.868"></polygon> <rect height="1" width="1" x="4" y="4"></rect> <polygon points="6.817 5 6 5 6 6 6.38 6 6.817 5"></polygon> <rect height="1" width="1" x="2" y="6"></rect> <rect height="1" width="1" x="3" y="5"></rect> <rect height="1" width="1" x="4" y="7"></rect> <polygon points="4 11.439 4 11 3 11 3 12 3.755 12 4 11.439"></polygon> <rect height="1" width="1" x="2" y="12"></rect> <rect height="1" width="1" x="2" y="9"></rect> <rect height="1" width="1" x="2" y="15"></rect> <polygon points="4.63 10 4 10 4 11 4.192 11 4.63 10"></polygon> <rect height="1" width="1" x="3" y="8"></rect> <path d="M10.832,4.2L11,4.582V4H10.708A1.948,1.948,0,0,1,10.832,4.2Z"></path> <path d="M7,4.582L7.168,4.2A1.929,1.929,0,0,1,7.292,4H7V4.582Z"></path> <path d="M8,13H7.683l-0.351.8a1.933,1.933,0,0,1-.124.2H8V13Z"></path> <rect height="1" width="1" x="12" y="2"></rect> <rect height="1" width="1" x="11" y="3"></rect> <path d="M9,3H8V3.282A1.985,1.985,0,0,1,9,3Z"></path> <rect height="1" width="1" x="2" y="3"></rect> <rect height="1" width="1" x="6" y="2"></rect> <rect height="1" width="1" x="3" y="2"></rect> <rect height="1" width="1" x="5" y="3"></rect> <rect height="1" width="1" x="9" y="2"></rect> <rect height="1" width="1" x="15" y="14"></rect> <polygon points="13.447 10.174 13.469 10.225 13.472 10.232 13.808 11 14 11 14 10 13.37 10 13.447 10.174"></polygon> <rect height="1" width="1" x="13" y="7"></rect> <rect height="1" width="1" x="15" y="5"></rect> <rect height="1" width="1" x="14" y="6"></rect> <rect height="1" width="1" x="15" y="8"></rect> <rect height="1" width="1" x="14" y="9"></rect> <path d="M3.775,14H3v1H4V14.314A1.97,1.97,0,0,1,3.775,14Z"></path> <rect height="1" width="1" x="14" y="3"></rect> <polygon points="12 6.868 12 6 11.62 6 12 6.868"></polygon> <rect height="1" width="1" x="15" y="2"></rect> <rect height="1" width="1" x="12" y="5"></rect> <rect height="1" width="1" x="13" y="4"></rect> <polygon points="12.933 9 13 9 13 8 12.495 8 12.933 9"></polygon> <rect height="1" width="1" x="9" y="14"></rect> <rect height="1" width="1" x="8" y="15"></rect> <path d="M6,14.926V15H7V14.316A1.993,1.993,0,0,1,6,14.926Z"></path> <rect height="1" width="1" x="5" y="15"></rect> <path d="M10.668,13.8L10.317,13H10v1h0.792A1.947,1.947,0,0,1,10.668,13.8Z"></path> <rect height="1" width="1" x="11" y="15"></rect> <path d="M14.332,12.2a1.99,1.99,0,0,1,.166.8H15V12H14.245Z"></path> <rect height="1" width="1" x="14" y="15"></rect> <rect height="1" width="1" x="15" y="11"></rect> </g> <polyline class="ql-stroke" points="5.5 13 9 5 12.5 13"></polyline> <line class="ql-stroke" x1="11.63" x2="6.38" y1="11" y2="11"></line> </svg>';


  }
  editorContentChanged(event): void {
    this.sdk.setContent(event.html);

      // Referencing wrong getData
    this.sdk.getData((data: any) => {
      if (!data) {
        return;
      }
      const numberOfEdits = data.numberOfEdits || 0;
      this.sdk.setData({
        numberOfEdits: numberOfEdits + 1
      });
    });
    this.sdk.getCentralData( (central) => {
      if (central) {
        const totalNumberOfEdits = central.totalNumberOfEdits || 0;
        this.sdk.setCentralData({
          totalNumberOfEdits: totalNumberOfEdits + 1
        });
      }
    });


  }

  quillInit(): void {
    this.sdk.getContent((content) => this.content = content);
  }

  onClose(reason: string): void{
    console.log(`Closed by ${reason}`);
  }

  mergeFields(value: any): void {
    const quill = this.editor.quillEditor;
    const range = quill.getSelection();
    if (range) {
      if ( typeof value === 'string') {
        if (!MergeFields.isClickable(value)) {
          return;
        }
        quill.insertText(range.index, MergeFields.getInsertValue(value));
      }
    }
  }
}
export class PremiumColors {

  constructor(private colorPalettes: ColorPalette[]) {}

  static getLengthColorPaletteIndexes(textType: string, colorSwatches: ColorPalette[]): string[]{
    let indexes = 0;
    const returnString = [];
    for (const cs of colorSwatches){
      returnString.push(`${indexes}`);
      indexes++;
      for (const clp of cs.color_swatches.filter(x => x.text_types.includes(textType))){
        returnString.push(`${indexes}`);
        indexes++;
      }
    }
    return returnString;
  }

  getColorPaletteForIndex(value: number, textType: string): ColorSwatch | string {
    let bigArray = [];
    for (const colorPal of this.colorPalettes){
      const colorSwatches = colorPal.color_swatches.filter(x => x.text_types.includes(textType));
      bigArray.push(colorPal.palette_name);
      bigArray = bigArray.concat(colorSwatches);
    }
    return bigArray[value];
  }
}
class MergeFields {
 static mergeFields: MergeField[] = [
   { editorValue: 'First Name', insetValue: '%%firstname%%' },
   { editorValue: 'Last Name', insetValue: '%%lastname%%' },
   { editorValue: 'Email', insetValue: '%%emailaddr%%' },
   { editorValue: '$sep', insetValue: '' },
   { editorValue: 'Sender name', insetValue: '%%replyname%%' },
   { editorValue: 'Sender email', insetValue: '%%replyemailaddress%%' },
   { editorValue: 'Business address', insetValue: '%%member_busname%%\n%%member_addr%%\n%%member_city%%, %%member_state%% %%member_postalcode%%\n%%member_country%%' },
   { editorValue: '$sep', insetValue: '' },
   { editorValue: 'Subscription center URL', insetValue: '%%subscription_center_url%%' },
   { editorValue: 'Profile center URL', insetValue: '%%profile_center_url%%' },
   { editorValue: 'Unsubscribe URL', insetValue: '%%unsub_center_url%%' },

 ];
  private i: number;
  public static getFieldIndexesList(): string[] {
    const indexList: string[] = [];
    for (let i = 0; i < this.mergeFields.length; i ++){
      indexList.push(String(i));
    }
    return indexList;

  }
  public static getInsertValue(value: string): string {
    try{
      return this.mergeFields[Number(value)].insetValue;
    }catch (e) {
      return '';
    }
  }
  public static isClickable(value: string): boolean{
    try{
      return !this.mergeFields[Number(value)].editorValue.startsWith('$');
    }catch (e) {
      return false;
    }
  }
  public static getDisplayValue(value: string): string {
    try{
      return this.mergeFields[Number(value)].editorValue;
    }catch (e) {
      return '';
    }
  }


}
